/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {RadioGroup} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';

export interface BulkSCIInfoProps {
  classes: {
    [key: string]: string;
  };
  selectedSCITypesDevices: Array<any>;
  selectedSCITypes: Array<any>;
  selectedSCILength: Array<any>;
  setSelectedDevicetype: Function;
  selectedDevicetype: any;
  selectedNonSCIDevices: Array<any>;
  setSelectedSCITypesDevices: Function;
  setBulkSettingPersist: Function;
}

export interface GetOptions {
  value: string | boolean | any;
  label: string | any;
};

export type GetOptionsResult = GetOptions[];

// defines the type definitions for import params ...
export type ImportParamsType = {
  fileName?: string;
  fileSize?: number;
  isComplete?: boolean;
  isFailure?: boolean,
  items?: Array<any>;
}

/**
 * Display Selected SCI and Non SCI devices
 */
export const BulkSCIInfo = ({
  classes,
  selectedSCITypesDevices,
  selectedSCITypes,
  selectedSCILength,
  setSelectedDevicetype,
  selectedDevicetype,
  selectedNonSCIDevices,
  setSelectedSCITypesDevices,
  setBulkSettingPersist
}: BulkSCIInfoProps) => {
  const [showSnackBar, setShowSnackBar] = useState(false);

  const handleTypeChange = useCallback((event) => {
    setSelectedDevicetype(event.target.value);
    setBulkSettingPersist({});
  }, [selectedSCITypesDevices]);

  const selectedSCTypeRemove = useCallback((id, index=null, allSCTypesDevices) => {
    if (selectedSCITypesDevices.flat().length === 1) {
      setShowSnackBar(true);
      return;
    }
    const allDevice = [...allSCTypesDevices];
    const selectedType = index ? allSCTypesDevices[index] : allSCTypesDevices[0];
    selectedType.forEach((device, i) => {
      if (device.id === id) {
        allDevice[index || 0].splice(i, 1);
      }
    });
    setSelectedSCITypesDevices(allDevice);
  }, [selectedSCITypesDevices]);

  const getSelectedSCILabel = useCallback((indx) => {
    const scType = selectedSCITypes.find((elem, index) => index === indx);
    const scLength = selectedSCILength.find((elm, index) => index === indx);
    return `${scType} ${scLength} Devices`;
  }, [selectedSCITypes, selectedSCILength]);

  function closeSnack() {
    setShowSnackBar(false);
  }

  return (
    <div className={classes.sciInfoContent}>
      {selectedSCITypesDevices.length === 0 && (
        <div className={classes.sciInfoText}>
          {translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.noDevices')}
        </div>
      )}
      {selectedSCITypesDevices.length > 1 && (
        <>
          <div className={classes.sciInfoText}>
            {translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.title')}
            <Tooltip arrow
              title={translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.toolTipMessage')}
              placement={'top'}>
              <span className={classes.infoIcon}>
                <InfoIcon size={22} stroke={dlsColor('grey3')} />
              </span>
            </Tooltip>
          </div>
          <div className={classes.sciSubTitleText}>
            {translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.subTitle')}
          </div>
          <div>
            <FormItem disableMinWidth customWidth={12}>
              <RadioGroup value={selectedDevicetype}>
                {selectedSCITypesDevices.map((type, indx) => {
                  return (
                    <span className={classes.sciInfoText}>
                      <RadioButton onChange={handleTypeChange}
                        checked={selectedDevicetype === type?.[0]?.typeName}
                        label={<span className={classes.sciSubTitleText}>{getSelectedSCILabel(indx)}</span>}
                        value={type.length > 0 && type[0].typeName}
                        disabled={!type.length}/>
                      <br />
                      <div>
                        {type.length ? type.map((device, index) => (
                          <Chip label={device?.name}
                            size='small'
                            maxWidth={false}
                            borderRadius='56px'
                            labelColor={dlsColor('grey2')}
                            hoverDefault
                            showCustomDeleteIcon
                            noTextTransform
                            isTopSpacing
                            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
                            onDelete={function() {
                              selectedSCTypeRemove(device?.id, indx, selectedSCITypesDevices);
                            }}/>
                        )) : ''}
                      </div>
                    </span>
                  );
                })}
              </RadioGroup>
            </FormItem>
          </div>
        </>
      )}
      {selectedSCITypesDevices.length === 1 && (
        <>
          <div className={classes.nonSCIText}>
            {translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.selectedDevices', {deviceType: selectedSCITypes[0]})}
          </div>
          <span className={classes.sciInfoText}>
            {selectedSCITypesDevices[0].length ? selectedSCITypesDevices[0].map((device, index) => (
              <Chip label={device?.name}
                size='small'
                maxWidth={false}
                borderRadius='56px'
                labelColor={dlsColor('grey2')}
                hoverDefault
                showCustomDeleteIcon
                noTextTransform
                customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
                onDelete={function() {
                  selectedSCTypeRemove(device?.id, null, selectedSCITypesDevices);
                }}/>
            )) : ''}
          </span>
        </>
      )}
      {(selectedNonSCIDevices.length > 0 && selectedSCITypesDevices.length > 0) && (
        <>
          <div className={classes.nonSCIText}>
            {translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.nonSCIDevicesMessage')}
          </div>
          {selectedNonSCIDevices.length ? selectedNonSCIDevices.map((device, index) => (
            <Chip label={device?.name}
              size='small'
              maxWidth={false}
              borderRadius='56px'
              labelColor={dlsColor('grey2')}
              hoverDefault
              showCustomDeleteIcon
              noTextTransform
              isTopSpacing/>
          )) : ''}
        </>
      )}
      <IonSnackbar show={showSnackBar}
        message={translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.removeDeviceMessage')}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={closeSnack}
        onClose={closeSnack}
        autoHideDuration={3000} />
    </div>
  );
};

export default BulkSCIInfo;
